import { useMemo } from 'react';
import { format } from 'date-fns';
import { PenTool, X, ExternalLink, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import type { Deal } from '@/hooks/use-deals';
import type { SignableNote } from '../types';
import { RelatedDomainsSection } from './related-domains';

interface DetectedLinksProps {
  content: string;
}

function DetectedLinks({ content }: DetectedLinksProps) {
  const links = useMemo(() => {
    if (!content)
      return [];

    // URL regex pattern that matches both http:// and https://
    const urlPattern = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
    return Array.from(content.matchAll(urlPattern), (match) => match[0]);
  }, [content]);

  if (links.length === 0)
    return null;

  return (
    <div className="space-y-1 rounded-lg border p-4">
      <div className="text-sm font-medium">Detected Links:</div>
      <div className="space-y-2">
        {links.map((link, index) => (
          <div key={index} className="flex items-center gap-2 break-words text-sm">
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="overflow-hidden break-all text-blue-600 hover:underline"
            >
              {link}
              <ExternalLink className="ml-1 inline-block size-3" />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

interface SignableNoteProps {
  item: SignableNote;
  onUpdateContent: (id: string, content: string) => void;
  onSign: (id: string, role: 'creator' | 'reviewer') => void;
  userId: string | undefined;
  deal: Deal;
  onConvertToGleif?: (id: string) => void;
  isConverting?: boolean;
}

export function SignableNoteComponent({
  item,
  onUpdateContent,
  onSign,
  userId,
  deal,
  onConvertToGleif,
  isConverting,
}: SignableNoteProps) {
  const isGleifNote = item.title.trim().toLowerCase() === 'imported gleif info';
  const isMarketingInfo = item.title.trim().toLowerCase() === 'marketing information';

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        <textarea
          value={item.content}
          onChange={(e) => onUpdateContent(item.id, e.target.value)}
          className="h-32 w-full rounded-md border bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          placeholder="Enter note content"
        />
        <DetectedLinks content={item.content} />
        {isMarketingInfo && (
          <RelatedDomainsSection deal={deal} currentDomains={deal.domains} />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {isGleifNote && onConvertToGleif && (
          <Button
            variant="secondary"
            className="flex-1 gap-2"
            disabled={isConverting}
            onClick={() => onConvertToGleif(item.id)}
          >
            {isConverting
              ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Converting...
                  </>
                )
              : (
                  <>
                    <Sparkles className="size-4" />
                    Convert to GLEIF Check
                  </>
                )}
          </Button>
        )}
        {item.id === 'name-selection' && (
          <Button
            variant="outline"
            className="flex-1 gap-2"
            onClick={() => {
              onUpdateContent(item.id, `Domain(s) comply with Name Selection Policy${item.content ? `\n\n${item.content}` : ''}`);
              toast.success('Default text applied');
            }}
          >
            Default
          </Button>
        )}
        {item.id === 'employment-authority' && (
          <Button
            variant="outline"
            className="flex-1 gap-2"
            onClick={() => {
              const contacts = deal.verification_contacts.people;
              if (!contacts || contacts.length === 0) {
                toast.error('No verification contacts found');
                return;
              }
              const contactsText = contacts
                .map((contact) => `By: ${contact.name}, ${contact.title}\nHow: via eMail (SSD)`)
                .join('\n\n');
              onUpdateContent(item.id, contactsText);
              toast.success('Contact information applied');
            }}
          >
            Default
          </Button>
        )}
        <Button
          variant="outline"
          className="flex-1 gap-2"
          onClick={() => onSign(item.id, 'creator')}
        >
          {item.signatures.some(
            (s) => s.userId === userId && s.role === 'creator',
          )
            ? (
                <>
                  <X className="size-4" />
                  Remove Verifier Signature
                </>
              )
            : (
                <>
                  <PenTool className="size-4" />
                  Sign as Verifier
                </>
              )}
        </Button>
        <Button
          variant="outline"
          className="flex-1 gap-2"
          onClick={() => onSign(item.id, 'reviewer')}
        >
          {item.signatures.some(
            (s) => s.userId === userId && s.role === 'reviewer',
          )
            ? (
                <>
                  <X className="size-4" />
                  Remove Reviewer Signature
                </>
              )
            : (
                <>
                  <PenTool className="size-4" />
                  Sign as Reviewer
                </>
              )}
        </Button>
      </div>
      {item.signatures.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium">Signatures:</div>
          {item.signatures.map((signature, index) => (
            <div
              key={index}
              className="text-sm text-muted-foreground"
            >
              {signature.name}
              {' '}
              (
              {signature.role}
              ) -
              {' '}
              {format(new Date(signature.timestamp), 'MMM d, yyyy h:mm a')}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
